/**
 * API のエラー（FR-3.3）。
 *
 * サーバは RFC 7807（problem+json）で返す。そうでない応答
 * （プロキシのエラーページなど）でも落ちないようにする。
 *
 * **メッセージにトークンを入れない**（NFR-3）。
 * ここに認証ヘッダを混ぜると、利用側のログに秘密が流れる。
 */

export interface Problem {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail?: string;
  readonly instance?: string;
  readonly code: string;
  readonly retriable?: boolean;
  readonly errors?: readonly unknown[];
}

export class RcaApiError extends Error {
  override readonly name = "RcaApiError";

  constructor(readonly problem: Problem) {
    super(problem.detail ?? problem.title);
  }

  get status(): number {
    return this.problem.status;
  }

  get code(): string {
    return this.problem.code;
  }

  /** サーバが「再試行してよい」と示しているか。 */
  get retriable(): boolean {
    return this.problem.retriable === true;
  }

  /** 認証し直せば解決する種類か。 */
  get isAuthError(): boolean {
    return this.problem.status === 401;
  }
}

/** ネットワーク到達前の失敗（DNS・切断など）。 */
export class RcaNetworkError extends Error {
  override readonly name = "RcaNetworkError";

  constructor(
    message: string,
    override readonly cause?: unknown,
  ) {
    super(message);
  }
}

/**
 * 応答を `Problem` に変換する。
 *
 * 本文が problem+json でなくても、必ず何かを返す（例外にしない）。
 */
export async function toProblem(response: Response): Promise<Problem> {
  try {
    const body = (await response.clone().json()) as Partial<Problem>;
    if (typeof body.code === "string" && typeof body.status === "number") {
      return body as Problem;
    }
  } catch {
    // JSON でない応答。下のフォールバックへ。
  }
  return {
    type: "about:blank",
    title: response.statusText === "" ? "Request failed" : response.statusText,
    status: response.status,
    code: "http_error",
  };
}
